import { useMemo } from 'react';
import { useQuery } from 'data-fetching-client';
import { getFormattedPersonalizationProperties } from 'automation-ui-action-features/queries/utils/getFormattedPersonalizationProperties';
import { getOwnerPropertyOptions } from 'automation-ui-action-features/queries/utils/getOwnerPropertyOptions';
import { GET_OBJECT_PROPERTIES_BY_GROUP } from 'automation-ui-references/object-type-properties/ObjectTypePropertyQueries';
// @ts-expect-error untyped module customer-data-objects-ui-components

import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { ENFORCE_FIELD_LEVEL_PERMISSIONS, SUPPORT_SELECTING_SENSITIVE_PROPERTIES } from 'automation-ui-action-features/constants/Gates';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Decorator for fetching crm properties for merge tags/Personalization tokens.
 *
 * It requires the props:
 * - propertyTypeId - String object type id of the object being edited. Ex: 0-1
 *
 * It provides the props:
 * - formattedPropertiesByGroup - Array of properties by group, filtered by block list and hidden properties, and sorted
 * - propertiesByGroup - Deprecated: use formattedPropertiesByGroup instead
 * - allPropertiesByGroup - Array of properties by group, unchanged as returned from properties service
 * - propertiesByGroupError - Error object
 * - propertiesByGroupLoading - Boolean of loading status
 */
export const queryObjectProperties = WrappedComponent => props => {
  const {
    loading,
    error,
    data
  } = useQuery(GET_OBJECT_PROPERTIES_BY_GROUP, {
    skip: !props.propertyTypeId,
    variables: {
      objectTypeId: props.propertyTypeId
    }
  });
  const propertiesByGroup = data === null || data === void 0 ? void 0 : data.automation_propertiesByGroup;
  const formattedPropertiesByGroup = useMemo(() => propertiesByGroup ? getFormattedPersonalizationProperties({
    propertiesByGroup
  }) : [], [propertiesByGroup]);
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    propertiesByGroup: formattedPropertiesByGroup,
    formattedPropertiesByGroup: formattedPropertiesByGroup,
    allPropertiesByGroup: propertiesByGroup || [],
    propertiesByGroupLoading: loading,
    propertiesByGroupError: error
  }));
};
export function useOwnerPropertyOptions({
  objectTypeId,
  includeMultiSelectProperties
}) {
  var _data$automation_prop;
  const {
    data,
    loading,
    refetch
  } = useQuery(GET_OBJECT_PROPERTIES_BY_GROUP, {
    skip: !objectTypeId,
    variables: {
      objectTypeId: objectTypeId
    }
  });
  const isGatedToSupportSelectingSensitiveProperties = !useIsUngated(SUPPORT_SELECTING_SENSITIVE_PROPERTIES);
  const enforceFlp = useIsUngated(ENFORCE_FIELD_LEVEL_PERMISSIONS);
  const propertiesByGroup = (_data$automation_prop = data === null || data === void 0 ? void 0 : data.automation_propertiesByGroup) !== null && _data$automation_prop !== void 0 ? _data$automation_prop : [];
  const ownerPropertyOptions = getOwnerPropertyOptions({
    dataSensitivityAllowlist: isGatedToSupportSelectingSensitiveProperties ? ['none'] : undefined,
    propertiesByGroup,
    objectTypeId: objectTypeId,
    enforceFlp,
    includeMultiSelectProperties
  });
  return {
    ownerPropertyLoading: loading,
    ownerPropertyOptions,
    ownerPropertyRefetch: refetch
  };
}
/**
 * Decorator for fetching crm properties for Owner property select options.
 *
 * It requires the props:
 * - flowObjectTypeId|propertyTypeId|objectTypeId - String object type id of the object being edited.
 *
 * It provides the props:
 * - ownerPropertyLoading - Boolean of loading status
 * - ownerPropertyOptions - Array of text/value options for choosing an OWNER-type property
 */
export const queryOwnerPropertyOptions = WrappedComponent => props => {
  const objectTypeId = props.flowObjectTypeId || props.propertyTypeId || props.objectTypeId;
  const includeMultiSelectProperties = props.includeMultiSelectProperties;
  const {
    ownerPropertyLoading,
    ownerPropertyOptions,
    ownerPropertyRefetch
  } = useOwnerPropertyOptions({
    objectTypeId,
    includeMultiSelectProperties
  });
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    ownerPropertyLoading: ownerPropertyLoading,
    ownerPropertyOptions: ownerPropertyOptions,
    ownerPropertyRefetch: ownerPropertyRefetch
  }));
};
export default queryObjectProperties;